import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Footer from "@/components/public/Footer";

export default function PublicLayout({ children }) {
  const [nav, setNav] = useState([]);
  const [logo, setLogo] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/website/nav").then((r) => r.data || []),
      api.get("/website/pages").then((r) => r.data || []),
    ]).then(([navItems, pages]) => {
      const known = new Set(pages.map((p) => p.slug));
      const navVisible = new Set(pages.filter((p) => p.active !== false && p.nav_visible !== false).map((p) => p.slug));
      const merged = (navItems || []).filter((n) => {
        if (!n.visible) return false;
        if (n.external) return true;
        if (known.has(n.path)) return navVisible.has(n.path);
        return true;
      });
      setNav(merged);
    });
    api.get("/website/logo").then(({ data }) => setLogo(data));
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const dashPath = user ? (user.effective_permissions?.length ? "/admin" : user.roles?.includes("secretary") ? "/secretaire" : "/espace") : "/connexion";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className={`sticky top-0 z-50 transition-all duration-200 ${scrolled ? "bg-white/90 backdrop-blur-xl border-b border-border shadow-sm" : "bg-white/70 backdrop-blur-md border-b border-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-16 py-2.5 flex items-center justify-between gap-4">
          <Logo className="hidden lg:flex shrink-0" mediaId={logo?.versions?.header} size={logo?.sizes?.header_width} />
          <Logo className="flex lg:hidden shrink-0" mediaId={logo?.versions?.mobile || logo?.versions?.header} size={logo?.sizes?.mobile_width} />
          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((n) => (
              n.external ? (
                <a key={n.path} href={n.path} target={n.target || "_self"} rel="noreferrer"
                  className="px-3 py-2 text-sm font-medium rounded-md text-slate-600 hover:text-primary hover:bg-secondary transition-colors">{n.label}</a>
              ) : (
                <Link key={n.path} to={n.path} data-testid={`nav-${n.path}`}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${loc.pathname === n.path ? "text-primary" : "text-slate-600 hover:text-primary hover:bg-secondary"}`}>
                  {n.label}
                </Link>
              )
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(dashPath)} data-testid="nav-login-btn">
              {user ? "Mon espace" : "Connexion"}
            </Button>
            <Button size="sm" className="bg-primary hover:opacity-95" onClick={() => navigate("/demande")} data-testid="nav-request-btn">
              Faire une demande <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] sm:w-96 p-0">
              <div className="p-5 border-b border-border flex items-center justify-between">
                <Logo mediaId={logo?.versions?.mobile || logo?.versions?.header} size={logo?.sizes?.mobile_width} />
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)}><X className="h-5 w-5" /></Button>
              </div>
              <div className="p-4 flex flex-col gap-1">
                {nav.map((n) => (
                  n.external
                    ? <a key={n.path} href={n.path} target={n.target || "_self"} rel="noreferrer" className="px-3 py-3 rounded-md text-slate-700 hover:bg-secondary font-medium">{n.label}</a>
                    : <Link key={n.path} to={n.path} className="px-3 py-3 rounded-md text-slate-700 hover:bg-secondary font-medium">{n.label}</Link>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  <Button variant="outline" onClick={() => navigate(dashPath)}>{user ? "Mon espace" : "Connexion"}</Button>
                  <Button className="bg-primary" onClick={() => navigate("/demande")}>Faire une demande</Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
